import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, Paper, Alert, CircularProgress,
  Accordion, AccordionSummary, AccordionDetails, Checkbox, FormControlLabel,
  Chip, Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MergeIcon from '@mui/icons-material/MergeType';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { listProjects, combineProjects } from '../../api/annotatorProjects';
import type { AnnotatorProject } from '../../types/annotator';

interface Selection {
  [projectId: number]: Set<number>; // projectId -> set of selected label ids
}

export default function AnnotatorCombineProjectsPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<AnnotatorProject[]>([]);
  const [selection, setSelection] = useState<Selection>({});
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    listProjects()
      .then((data) => setProjects([...data].sort((a, b) => a.name.localeCompare(b.name))))
      .catch(() => setError('Failed to load projects.'))
      .finally(() => setLoading(false));
  }, []);

  function toggleLabel(projectId: number, labelId: number) {
    setSelection((prev) => {
      const next = { ...prev };
      const current = new Set(next[projectId] ?? []);
      if (current.has(labelId)) {
        current.delete(labelId);
      } else {
        current.add(labelId);
      }
      next[projectId] = current;
      return next;
    });
  }

  function toggleAllLabels(project: AnnotatorProject) {
    const currentIds = selection[project.id] ?? new Set<number>();
    const allIds = project.labels.map((l) => l.id);
    const allSelected = allIds.every((id) => currentIds.has(id));
    setSelection((prev) => ({
      ...prev,
      [project.id]: allSelected ? new Set() : new Set(allIds),
    }));
  }

  const totalSelectedLabels = Object.values(selection).reduce((sum, s) => sum + s.size, 0);

  const sources = Object.entries(selection)
    .map(([pid, labelSet]) => ({
      project_id: Number(pid),
      label_ids: Array.from(labelSet),
    }))
    .filter((s) => s.label_ids.length > 0);

  async function handleCombine() {
    if (!newName.trim()) {
      setError('New project name is required.');
      return;
    }
    if (sources.length === 0) {
      setError('Select at least one class from a project.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const created = await combineProjects({
        name: newName.trim(),
        description: newDescription.trim() || undefined,
        organisation_id: projects[0].organisation_id,
        sources,
      });
      navigate(`/annotator/projects/${created.id}`);
    } catch {
      setError('Failed to combine projects. Check that all source projects are accessible.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/annotator/projects')} size="small">
          Projects
        </Button>
        <Typography variant="h5" fontWeight={600}>Combine Projects</Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select classes from one or more projects. A new project will be created containing those classes and all their annotated images.
      </Typography>

      {/* New project name */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>New Project</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Project Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            required
            size="small"
            autoFocus
          />
          <TextField
            label="Description (optional)"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            size="small"
            multiline
            rows={2}
          />
        </Box>
      </Paper>

      {/* Project tree */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Select Classes
        {totalSelectedLabels > 0 && (
          <Chip label={`${totalSelectedLabels} selected`} size="small" color="primary" sx={{ ml: 1 }} />
        )}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : projects.length === 0 ? (
        <Typography color="text.secondary">No projects available.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
          {projects.map((project) => {
            const projectSelected = selection[project.id] ?? new Set<number>();
            const selectedCount = projectSelected.size;
            const totalLabels = project.labels.length;
            const allChecked = totalLabels > 0 && selectedCount === totalLabels;
            const indeterminate = selectedCount > 0 && selectedCount < totalLabels;

            return (
              <Accordion key={project.id} disableGutters elevation={1}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', mr: 1 }}>
                    <Checkbox
                      checked={allChecked}
                      indeterminate={indeterminate}
                      onChange={() => toggleAllLabels(project)}
                      onClick={(e) => e.stopPropagation()}
                      size="small"
                      disabled={totalLabels === 0}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight={600}>{project.name}</Typography>
                      {project.description && (
                        <Typography variant="caption" color="text.secondary">{project.description}</Typography>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {totalLabels} class{totalLabels !== 1 ? 'es' : ''}
                      {selectedCount > 0 && ` · ${selectedCount} selected`}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {project.labels.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
                      No classes defined.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pl: 1 }}>
                      {project.labels.map((label) => (
                        <FormControlLabel
                          key={label.id}
                          control={
                            <Checkbox
                              checked={projectSelected.has(label.id)}
                              onChange={() => toggleLabel(project.id, label.id)}
                              size="small"
                              sx={{ color: label.color, '&.Mui-checked': { color: label.color } }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  backgroundColor: label.color,
                                  flexShrink: 0,
                                }}
                              />
                              <Typography variant="body2">{label.name}</Typography>
                            </Box>
                          }
                          sx={{ mr: 2 }}
                        />
                      ))}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Divider sx={{ mb: 2 }} />

      {/* Summary + action */}
      {sources.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Summary</Typography>
          {sources.map((src) => {
            const proj = projects.find((p) => p.id === src.project_id);
            return (
              <Box key={src.project_id} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                <Typography variant="body2" fontWeight={500}>{proj?.name}:</Typography>
                {src.label_ids.map((lid) => {
                  const lbl = proj?.labels.find((l) => l.id === lid);
                  return lbl ? (
                    <Chip
                      key={lid}
                      label={lbl.name}
                      size="small"
                      sx={{ backgroundColor: lbl.color + '33', borderColor: lbl.color, border: '1px solid' }}
                    />
                  ) : null;
                })}
              </Box>
            );
          })}
        </Paper>
      )}

      <Button
        variant="contained"
        startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <MergeIcon />}
        onClick={handleCombine}
        disabled={submitting || totalSelectedLabels === 0 || !newName.trim()}
        size="large"
      >
        {submitting ? 'Combining…' : 'Combine into New Project'}
      </Button>
    </Box>
  );
}
